import { Tooltip } from '@mui/material'
import { Box } from '@mui/system'
import { FeatureFlagIdBundleAutomationEmergencyShutoff, FeatureFlagIdDisableAllAutomations, FeatureFlagIdEmergencyNotificationShutoff, FeatureFlagIdSystemManagement, FeatureFlagIdWorkflowAutomationEmergencyShutoff } from 'c1gen/features'
import { SettingsV1SystemManagementServiceGetSystemManagementConfig } from 'c1gen/permissions'
import { C1Icon } from 'components/core/icons/C1Icon'
import { useFeatureChecker, useHasPermission } from 'components/no-ui/capability'
import { TriangleAlert } from 'lucide-react'
import { GetSystemManagementConfigRequest } from 'pbts/c1/api/settings/v1/system_management'
import { FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getSystemManagementConfig } from 'redux_toolkit/system-management'

export const SystemManagementWarningIndicator: FC = () => {
    const intl = useIntl()
    const hasSystemManagementFeature = useFeatureChecker(FeatureFlagIdSystemManagement)
    const hasSystemManagementPermission = useHasPermission(SettingsV1SystemManagementServiceGetSystemManagementConfig)

    const isNotificationsSystemDisabled = useFeatureChecker(FeatureFlagIdEmergencyNotificationShutoff)
    const isBundleAutomationsSystemDisabled = useFeatureChecker(FeatureFlagIdBundleAutomationEmergencyShutoff)
    const isWorkflowAutomationsSystemDisabled = useFeatureChecker(FeatureFlagIdWorkflowAutomationEmergencyShutoff)
    const isAllAutomationsSystemDisabled = useFeatureChecker(FeatureFlagIdDisableAllAutomations)

    const { data: configData } = getSystemManagementConfig.useGetSystemManagementConfig(
        GetSystemManagementConfigRequest.create({}),
        { actionable: !!hasSystemManagementFeature && hasSystemManagementPermission },
    )

    if (!hasSystemManagementFeature || !configData) {
        return null
    }

    const disabledSettings: string[] = []

    if (configData.notificationsShutoff || isNotificationsSystemDisabled) {
        disabledSettings.push(intl.formatMessage({
            id: 'system_management_notifications_disabled_short',
            defaultMessage: 'Notifications',
        }))
    }
    if (configData.bundleAutomationsShutoff || isBundleAutomationsSystemDisabled) {
        disabledSettings.push(intl.formatMessage({
            id: 'system_management_bundle_automations_disabled_short',
            defaultMessage: 'Access profile enrollments',
        }))
    }
    if (configData.workflowAutomationsShutoff || isWorkflowAutomationsSystemDisabled) {
        disabledSettings.push(intl.formatMessage({
            id: 'system_management_workflow_automations_disabled_short',
            defaultMessage: 'Automations',
        }))
    }
    if (isAllAutomationsSystemDisabled) {
        disabledSettings.push(intl.formatMessage({
            id: 'system_management_all_automations_disabled_short',
            defaultMessage: 'All automations',
        }))
    }

    if (disabledSettings.length === 0) {
        return null
    }

    const tooltipContent = (
        <Box>
            <FormattedMessage
                id="system_management_warning_tooltip_title"
                defaultMessage="System features disabled:"
            />
            <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                {disabledSettings.map((setting, index) => (
                    <li key={index}>{setting}</li>
                ))}
            </ul>
        </Box>
    )

    return (
        <Tooltip title={tooltipContent} placement="bottom">
            <C1Icon icon={TriangleAlert} sx={{ color: 'warning.main' }} />
        </Tooltip>
    )
}
